import {
  type PermissionCode,
  ATTENDANCE_PERMISSIONS,
  BILLING_PERMISSIONS,
  FIELD_OPS_PERMISSIONS,
  MARKET_PROMOTION_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  PROMOTION_PERMISSIONS,
  PUBLISH_PERMISSIONS,
  QUESTION_PERMISSIONS,
  RBAC_PERMISSIONS,
  REPORT_PERMISSIONS,
  SETTINGS_PERMISSIONS,
} from '@/constants/permissions';
import { PCMP_PERMISSION, type PcmpPermission } from '@/features/pcmp/api/pcmpAccess.api';
import {
  BarChart3,
  CalendarCheck,
  CalendarOff,
  BookOpen,
  ClipboardList,
  Gift,
  Coins,
  FileQuestion,
  BadgeDollarSign,
  Zap,
  Users,
  MessageSquare,
  Building2,
  MapPin,
  SlidersHorizontal,
  LayoutDashboard,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { USER_TYPE } from '@/features/users/constants/user.constants';
import { GEO_ACCESS, type GeoAccess } from '@/features/auth/utils/geo-access';

export interface NavVisibilityContext {
  userTypeId: number | null;
  departmentName: string | null;
  geoAccess: GeoAccess;
}

const isAdmin = (ctx: NavVisibilityContext): boolean =>
  ctx.userTypeId === USER_TYPE.SA || ctx.userTypeId === USER_TYPE.ADM;

const hasOfficeAccess = (ctx: NavVisibilityContext): boolean =>
  isAdmin(ctx) || ctx.geoAccess === GEO_ACCESS.OFFICE;

interface NavChild {
  label: string;
  href: string;
  usedBy?: string;
  activeWhen?: string[];
  upcoming?: boolean;
  isHidden?: boolean;
  visibleWhen?: (ctx: NavVisibilityContext) => boolean;
  requires?: PermissionCode;
  // PCMP keeps its own permission system, separate from the RBAC `requires`
  // above; a PCMP menu item is shown only when the viewer holds all of these.
  requiresPcmp?: PcmpPermission[];
}

interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  isHidden?: boolean;
  children: NavChild[];
}

export const ATTENDANCE_NAV: NavGroup[] = [
  {
    key: 'job_card',
    label: 'Job Card',
    icon: CalendarCheck,
    isHidden: true,
    children: [
      {
        label: 'Daily Dashboard',
        href: '/attendance',
        requires: ATTENDANCE_PERMISSIONS.DAILY_DASHBOARD_VIEW,
      },
      {
        label: 'Job Card',
        href: '/attendance/job-card',
        requires: ATTENDANCE_PERMISSIONS.JOB_CARD_VIEW,
        activeWhen: ['/attendance/employee-report'],
      },
      {
        label: 'Job Card (Night Shift)',
        href: '/attendance/shift-job-card',
        requires: ATTENDANCE_PERMISSIONS.SHIFT_JOB_CARD_VIEW,
        activeWhen: ['/attendance/shift-employee-report'],
      },
    ],
  },
];

export const HR_FORM_NAV: NavGroup[] = [
  {
    key: 'hr_form',
    label: 'HR Form',
    icon: CalendarOff,
    children: [
      {
        label: 'Leave Applications',
        href: '/hr-form/leave-applications',
      },
      {
        label: 'Leave Report',
        href: '/hr-form/leave-report',
        visibleWhen: hasOfficeAccess,
      },
      {
        label: 'Leave Types',
        href: '/settings/leave-types',
        visibleWhen: hasOfficeAccess,
      },
      { label: 'Holiday Calendar', href: '/settings/holidays', visibleWhen: hasOfficeAccess },
    ],
  },
];

export const GEO_LOCATION_NAV: NavGroup[] = [
  {
    key: 'geo_location',
    label: 'Geo Location',
    icon: MapPin,
    children: [
      {
        label: 'MO Travel History',
        href: '/geo-location/mo-travel-history',
      },
    ],
  },
];

export const USER_MANAGEMENT_NAV: NavGroup[] = [
  {
    key: 'user_management',
    label: 'User Management',
    icon: Users,
    isHidden: true,
    children: [
      { label: 'All Users', href: '/users', requires: RBAC_PERMISSIONS.USER_ALL_VIEW },
      { label: 'Roles', href: '/users/roles', requires: RBAC_PERMISSIONS.ROLE_VIEW },
      {
        label: 'Permissions',
        href: '/users/permissions',
        requires: RBAC_PERMISSIONS.ROLE_PERMISSION_EDIT,
      },
      {
        label: 'Admin Users',
        href: '/users/admin',
        requires: RBAC_PERMISSIONS.USER_ADMIN_VIEW,
        activeWhen: ['/users/create/admin'],
      },
      {
        label: 'Agent Users',
        href: '/users/agent',
        requires: RBAC_PERMISSIONS.USER_AGENT_VIEW,
        activeWhen: ['/users/create/agent'],
      },
      {
        label: 'General Users',
        href: '/users/general',
        requires: RBAC_PERMISSIONS.USER_GENERAL_VIEW,
        activeWhen: ['/users/create/general'],
      },
      {
        label: 'Marketing Users',
        href: '/users/marketing',
        requires: RBAC_PERMISSIONS.USER_MARKETING_VIEW,
        activeWhen: ['/users/create/marketing'],
      },
      {
        label: 'Special Users',
        href: '/users/special',
        requires: RBAC_PERMISSIONS.USER_SPECIAL_VIEW,
        activeWhen: ['/users/create/special'],
      },
    ],
  },
];

export const INSTITUTE_NAV: NavGroup[] = [
  {
    key: 'institute',
    label: 'Institute',
    icon: Building2,
    children: [
      {
        label: 'Institute Types',
        href: '/settings/institute-types',
        visibleWhen: hasOfficeAccess,
      },
      {
        label: 'View Institutes',
        href: '/settings/institutes',
        visibleWhen: hasOfficeAccess,
      },
      {
        label: 'Institute Mapping',
        href: '/settings/institutes/user-mapping',
        visibleWhen: hasOfficeAccess,
      },
      {
        label: 'Institute Submissions',
        href: '/settings/institute-submissions',
      },
      {
        label: 'Teacher Submissions',
        href: '/settings/teacher-submissions',
      },
      {
        label: 'Teacher Inactivation',
        href: '/settings/teacher-inactivations',
        visibleWhen: hasOfficeAccess,
      },
    ],
  },
];

export const FEEDBACK_NAV: NavGroup[] = [
  {
    key: 'feedback',
    label: 'Feedback',
    icon: MessageSquare,
    isHidden: true,
    children: [
      {
        label: 'View Feedbacks',
        href: '/feedback/list',
        requires: FIELD_OPS_PERMISSIONS.FEEDBACK_VIEW,
      },
      {
        label: 'Feedback Types',
        href: '/settings/feedback-types',
        requires: SETTINGS_PERMISSIONS.FEEDBACK_TYPES_EDIT,
      },
      { label: 'Surveys', href: '/feedback/surveys', requires: FIELD_OPS_PERMISSIONS.SURVEY_VIEW },
    ],
  },
];

export const ADMIN_NAV: NavGroup[] = [
  {
    key: 'admin',
    label: 'Admin',
    icon: SlidersHorizontal,
    isHidden: true,
    children: [
      {
        label: 'Limit Types',
        href: '/settings/limit-types',
        requires: SETTINGS_PERMISSIONS.LIMIT_TYPES_EDIT,
      },
      {
        label: 'Web URLs',
        href: '/settings/web-urls',
        requires: SETTINGS_PERMISSIONS.WEB_URLS_EDIT,
      },
      {
        label: 'App Version Check',
        href: '/reports/app-version-check',
        requires: REPORT_PERMISSIONS.APP_VERSION_VIEW,
      },
      {
        label: 'Menu Labels',
        href: '/settings/menu-labels',
        requires: SETTINGS_PERMISSIONS.MENU_LABELS_EDIT,
      },
    ],
  },
];

export const DASHBOARD_NAV: NavGroup[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    isHidden: true,
    children: [
      {
        label: 'System Dashboard',
        href: '/dashboards/system',
        requires: REPORT_PERMISSIONS.SYSTEM_DASHBOARD_VIEW,
      },
      {
        label: 'DCR Dashboard',
        href: '/dashboards/dcr',
        requires: REPORT_PERMISSIONS.DCR_DASHBOARD_VIEW,
      },
      {
        label: 'DER Dashboard',
        href: '/dashboards/der',
        requires: REPORT_PERMISSIONS.DER_DASHBOARD_VIEW,
      },
      {
        label: 'Specimen Closing',
        href: '/dashboards/zonal-statement',
        requires: REPORT_PERMISSIONS.ZONAL_STATEMENT_VIEW,
      },
      {
        label: 'DCR & DER Pending',
        href: '/dashboards/pending',
        requires: REPORT_PERMISSIONS.PENDING_VIEW,
      },
    ],
  },
];

export const BILLING_NAV: NavGroup[] = [
  {
    key: 'billing',
    label: 'Billing',
    icon: ReceiptText,
    isHidden: true,
    children: [
      {
        label: 'Offline DCR Verify',
        href: '/billing/offline-dcr-verify',
        requires: BILLING_PERMISSIONS.OFFLINE_DCR_VERIFY_VIEW,
      },
      {
        label: 'Offline DER Verify',
        href: '/billing/offline-der-verify',
        requires: BILLING_PERMISSIONS.OFFLINE_DER_VERIFY_VIEW,
      },
      {
        label: 'Expense Approve',
        href: '/billing/expense-approve',
        requires: BILLING_PERMISSIONS.EXPENSE_APPROVAL_VIEW,
      },
      {
        label: 'DCR Approve',
        href: '/billing/dcr-approve',
        requires: BILLING_PERMISSIONS.DCR_APPROVE_VIEW,
      },
      {
        label: 'DCR & DER Approval',
        href: '/billing/approval',
        requires: BILLING_PERMISSIONS.APPROVAL_VIEW,
      },
      {
        label: 'View All Cost',
        href: '/billing/all-cost',
        requires: BILLING_PERMISSIONS.ALL_COST_VIEW,
      },
      {
        label: 'DCR Cost Amendment',
        href: '/billing/cost-amendment',
        requires: BILLING_PERMISSIONS.COST_AMENDMENT_AMEND,
      },
      {
        label: 'Web Expense Info',
        href: '/billing/web-expense',
        requires: BILLING_PERMISSIONS.WEB_EXPENSES_CREATE,
      },
    ],
  },
];

export const PRODUCT_NAV: NavGroup[] = [
  {
    key: 'product',
    label: 'Product',
    icon: BookOpen,
    isHidden: true,
    children: [
      {
        label: 'Specimen Adjustment',
        href: '/product/specimen-adjustment',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_ADJUSTMENT_VIEW,
      },
      {
        label: 'View Specimen Institution',
        href: '/product/specimen-institution',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_INSTITUTION_VIEW,
      },
      {
        label: 'View DCR Date to Date',
        href: '/product/dcr-date-to-date',
        requires: PRODUCT_PERMISSIONS.DCR_DATE_TO_DATE_VIEW,
      },
      {
        label: 'View DCR Sheet',
        href: '/product/dcr-sheet',
        requires: PRODUCT_PERMISSIONS.DCR_SHEET_VIEW,
      },
      {
        label: 'Specimen List View',
        href: '/product/specimen-list-view',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_LIST_VIEW_VIEW,
      },
      {
        label: 'Specimen List',
        href: '/product/specimens',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_LIST_VIEW,
      },
      {
        label: 'Specimen Distribution',
        href: '/product/specimen-distribution',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_DISTRIBUTION_VIEW,
      },
      {
        label: 'Book Type',
        href: '/product/book-types',
        requires: PRODUCT_PERMISSIONS.BOOK_TYPE_VIEW,
      },
      {
        label: 'Book Edition',
        href: '/product/book-editions',
        requires: PRODUCT_PERMISSIONS.BOOK_EDITION_VIEW,
      },
    ],
  },
];

export const PRODUCT_REQUISITION_NAV: NavGroup[] = [
  {
    key: 'product_requisition',
    label: 'Product Requisition',
    icon: ClipboardList,
    isHidden: true,
    children: [
      {
        label: 'Specimen Requisition',
        href: '/product/specimen-requisition',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_REQUISITION_VIEW,
      },
      {
        label: 'Specimen Requisition Review',
        href: '/product/specimen-requisition-review',
        requires: PRODUCT_PERMISSIONS.SPECIMEN_REQUISITION_REVIEW_VIEW,
      },
      {
        label: 'SP Promotional Requisition',
        href: '/product/sp-promotional-requisition',
        requires: PRODUCT_PERMISSIONS.SP_PROMO_REQUISITION_VIEW,
      },
      {
        label: 'Booklisted Institution',
        href: '/product/booklisted-institution',
        activeWhen: ['/product/booklisted-institution'],
        requires: PRODUCT_PERMISSIONS.BOOKLISTED_INSTITUTE_VIEW,
      },
      {
        label: 'Mkt Student Collection Project',
        href: '/product/student-collection-projects',
        activeWhen: ['/product/student-collection-projects'],
        requires: PRODUCT_PERMISSIONS.STUDENT_PROJECT_VIEW,
      },
    ],
  },
];

export const PROMOTION_NAV: NavGroup[] = [
  {
    key: 'promotion',
    label: 'Promotion',
    icon: Gift,
    isHidden: true,
    children: [
      {
        label: 'Promotional Item Adjustment',
        href: '/promotion/adjustment',
        requires: PROMOTION_PERMISSIONS.ADJUSTMENT_VIEW,
      },
      {
        label: 'Promotional Item Distribution',
        href: '/promotion/distribution',
        requires: PROMOTION_PERMISSIONS.DISTRIBUTION_VIEW,
      },
      {
        label: 'Promotional Item List',
        href: '/promotion/items',
        requires: PROMOTION_PERMISSIONS.ITEM_LIST_VIEW,
      },
      {
        label: 'Promotional Item Type',
        href: '/promotion/item-types',
        requires: PROMOTION_PERMISSIONS.ITEM_TYPE_VIEW,
      },
    ],
  },
];

export const MARKET_PROMOTION_NAV: NavGroup[] = [
  {
    key: 'market_promotion',
    label: 'Market Promotion',
    icon: Coins,
    isHidden: true,
    children: [
      {
        label: 'Approval',
        usedBy: 'AM, Manager',
        href: '/market-promotion/approvals',
        requires: MARKET_PROMOTION_PERMISSIONS.APPROVAL_VIEW,
      },
      {
        label: 'MIS Approval',
        usedBy: 'MIS',
        href: '/market-promotion/mis-approvals',
        requires: MARKET_PROMOTION_PERMISSIONS.MIS_APPROVAL_VIEW,
      },
      {
        label: 'Publish Panel',
        usedBy: 'Sales',
        href: '/market-promotion/publish-panel',
        requires: MARKET_PROMOTION_PERMISSIONS.PUBLISH_PANEL_VIEW,
      },
      {
        label: 'Accounts Adjust',
        usedBy: 'Accounts',
        href: '/market-promotion/accounts-adjust',
        requires: MARKET_PROMOTION_PERMISSIONS.ACCOUNTS_ADJUST_VIEW,
      },
      {
        label: 'Market Promotion Project',
        href: '/market-promotion/projects',
        requires: MARKET_PROMOTION_PERMISSIONS.PROJECT_VIEW,
      },
      {
        label: 'Market Promotion Budget Entry',
        href: '/market-promotion/budget-entry',
        requires: MARKET_PROMOTION_PERMISSIONS.BUDGET_VIEW,
      },
      {
        label: 'Market Promotion Approval Report',
        href: '/market-promotion/approval-report',
        requires: MARKET_PROMOTION_PERMISSIONS.APPROVAL_REPORT_VIEW,
      },
      {
        label: 'Cash Collection',
        href: '/market-promotion/cash-collection',
        requires: MARKET_PROMOTION_PERMISSIONS.CASH_COLLECTION_VIEW,
      },
      {
        label: 'Distribution Summary',
        href: '/market-promotion/distribution-summary',
        requires: MARKET_PROMOTION_PERMISSIONS.DISTRIBUTION_SUMMARY_VIEW,
      },
      {
        label: 'Region-Wise Market Promotion',
        href: '/market-promotion/region-summary',
        requires: MARKET_PROMOTION_PERMISSIONS.REGION_SUMMARY_VIEW,
      },
      {
        label: 'Market Promotion Adjustment',
        href: '/market-promotion/adjustment',
        requires: MARKET_PROMOTION_PERMISSIONS.ADJUSTMENT_VIEW,
      },
      {
        label: 'Donation Requisition',
        href: '/market-promotion/requisitions',
        requires: MARKET_PROMOTION_PERMISSIONS.REQUISITION_VIEW,
      },
    ],
  },
];

export const QUESTION_NAV: NavGroup[] = [
  {
    key: 'question',
    label: 'Question',
    icon: FileQuestion,
    isHidden: true,
    children: [
      {
        label: 'Question Download by Class',
        href: '/question/download-by-class',
        requires: QUESTION_PERMISSIONS.DOWNLOAD_BY_CLASS_VIEW,
      },
      {
        label: 'Question Download by Board',
        href: '/question/download-by-board',
        requires: QUESTION_PERMISSIONS.DOWNLOAD_BY_BOARD_VIEW,
      },
      {
        label: 'Question Download',
        href: '/question/download',
        requires: QUESTION_PERMISSIONS.DOWNLOAD_VIEW,
      },
      {
        label: 'Question Upload',
        href: '/question/upload',
        requires: QUESTION_PERMISSIONS.UPLOAD_VIEW,
      },
      {
        label: 'Project Management',
        href: '/question/projects',
        requires: QUESTION_PERMISSIONS.PROJECT_VIEW,
      },
      {
        label: 'Subject Management',
        href: '/question/subjects',
        requires: QUESTION_PERMISSIONS.SUBJECT_VIEW,
      },
    ],
  },
];

export const PUBLISH_NAV: NavGroup[] = [
  {
    key: 'publish',
    label: 'Publish',
    icon: BadgeDollarSign,
    isHidden: true,
    children: [
      {
        label: 'Sales Publish Panel',
        href: '/publish/sales',
        requires: PUBLISH_PERMISSIONS.SALES_VIEW,
      },
      { label: 'MIS Publish Panel', href: '/publish/mis', requires: PUBLISH_PERMISSIONS.MIS_VIEW },
      {
        label: 'Accounts Disburse Panel',
        href: '/publish/accounts',
        requires: PUBLISH_PERMISSIONS.ACCOUNTS_VIEW,
      },
    ],
  },
];

export const PCMP_NAV: NavGroup[] = [
  {
    key: 'pcmp',
    label: 'PCMP',
    icon: BookOpen,
    isHidden: true,
    children: [
      {
        label: 'Projects',
        href: '/pcmp/projects',
        requiresPcmp: [PCMP_PERMISSION.PROJECT_MANAGE],
      },
      {
        label: 'Marketing Approval',
        href: '/pcmp/marketing',
        requiresPcmp: [PCMP_PERMISSION.VIEW],
      },
      {
        label: 'Payment Correction',
        href: '/pcmp/correction',
        requiresPcmp: [PCMP_PERMISSION.VIEW],
      },
      {
        label: 'MIS Verification',
        href: '/pcmp/mis',
        requiresPcmp: [PCMP_PERMISSION.VIEW, PCMP_PERMISSION.VIEW_ALL],
      },
      {
        label: 'Accounts Disbursement',
        href: '/pcmp/accounts',
        requiresPcmp: [PCMP_PERMISSION.VIEW, PCMP_PERMISSION.VIEW_ALL],
      },
      {
        label: 'Disburse Verify',
        href: '/pcmp/disburse-verify',
        requiresPcmp: [PCMP_PERMISSION.VIEW, PCMP_PERMISSION.MIS],
      },
      {
        label: 'Division Mapping',
        href: '/pcmp/department-divisions',
        requiresPcmp: [PCMP_PERMISSION.ADMIN],
      },
      {
        label: 'Edit Permissions',
        href: '/pcmp/edit-permissions',
        requiresPcmp: [PCMP_PERMISSION.ADMIN],
      },
    ],
  },
];

export const PCMP_PRIORITY_NAV: NavGroup[] = [
  {
    key: 'pcmp_priority',
    label: 'Priority Requests',
    icon: Zap,
    isHidden: true,
    children: [
      {
        label: 'Request Categories',
        href: '/pcmp/request-categories',
        requiresPcmp: [PCMP_PERMISSION.ADMIN],
      },
      {
        label: 'Request Proposals',
        href: '/pcmp/requests',
        activeWhen: ['/pcmp/requests/batches'],
        requiresPcmp: [PCMP_PERMISSION.VIEW],
      },
      {
        label: 'Requested Proposals',
        href: '/pcmp/requests/requested',
        requiresPcmp: [PCMP_PERMISSION.VIEW],
      },
    ],
  },
];

export const PCMP_REPORT_NAV: NavGroup[] = [
  {
    key: 'pcmp_reports',
    label: 'PCMP Reports',
    icon: BarChart3,
    isHidden: true,
    children: [
      {
        label: 'Pending Report',
        href: '/pcmp/reports/pending',
        requiresPcmp: [PCMP_PERMISSION.VIEW],
      },
      {
        label: 'Performance Report',
        href: '/pcmp/reports/performance',
        requiresPcmp: [PCMP_PERMISSION.VIEW_ALL],
      },
      {
        label: 'MO Wise Report',
        href: '/pcmp/reports/mo-wise',
        requiresPcmp: [PCMP_PERMISSION.VIEW_ALL],
      },
      {
        label: 'MIS Status Report',
        href: '/pcmp/reports/mis-status',
        requiresPcmp: [PCMP_PERMISSION.VIEW_ALL],
      },
    ],
  },
];

export interface NavSection {
  id: string;
  label?: string;
  groups: NavGroup[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'units',
    groups: [
      ...DASHBOARD_NAV,
      ...BILLING_NAV,
      ...PRODUCT_NAV,
      ...PRODUCT_REQUISITION_NAV,
      ...PROMOTION_NAV,
      ...MARKET_PROMOTION_NAV,
      ...QUESTION_NAV,
      ...PUBLISH_NAV,
      ...ADMIN_NAV,
      ...USER_MANAGEMENT_NAV,
      ...INSTITUTE_NAV,
      ...ATTENDANCE_NAV,
      ...HR_FORM_NAV,
      ...GEO_LOCATION_NAV,
      ...FEEDBACK_NAV,
    ],
  },
  {
    id: 'pcmp',
    groups: [...PCMP_NAV, ...PCMP_PRIORITY_NAV, ...PCMP_REPORT_NAV],
  },
];

export type { NavGroup, NavChild };

export interface NavReach {
  group: string;
  opens: { group: string; children: string[] }[];
  order: number;
}

function collectNavReach() {
  const childKeysByCode = new Map<string, Map<string, string[]>>();
  const groupOrder: string[] = [];
  const labelByKey = new Map<string, string>();

  for (const section of NAV_SECTIONS) {
    for (const group of section.groups) {
      if (labelByKey.has(group.key)) {
        throw new Error(`Two nav groups share the key ${group.key}`);
      }
      labelByKey.set(group.key, group.label);

      for (const child of group.children) {
        labelByKey.set(child.href, child.label);
      }

      if (group.isHidden) continue;

      for (const child of group.children) {
        if (child.isHidden || !child.requires) continue;
        if (!groupOrder.includes(group.key)) groupOrder.push(group.key);

        const byGroup = childKeysByCode.get(child.requires) ?? new Map<string, string[]>();
        byGroup.set(group.key, [...(byGroup.get(group.key) ?? []), child.href]);
        childKeysByCode.set(child.requires, byGroup);
      }
    }
  }

  const byPermission = new Map<string, NavReach>();
  const groupByPrefix = new Map<string, string>();

  for (const [code, byGroup] of childKeysByCode) {
    const opens = [...byGroup].map(([group, children]) => ({ group, children }));
    const first = opens[0];
    if (!first) continue;

    byPermission.set(code, { group: first.group, opens, order: byPermission.size });

    const [prefix] = code.split('.');
    if (prefix !== undefined && !groupByPrefix.has(prefix)) groupByPrefix.set(prefix, first.group);
  }

  return { byPermission, groupByPrefix, groupOrder, labelByKey };
}

const navReach = collectNavReach();

export const NAV_REACH_BY_PERMISSION: ReadonlyMap<string, NavReach> = navReach.byPermission;

export const NAV_GROUP_ORDER: readonly string[] = navReach.groupOrder;

export const NAV_LABEL_BY_KEY: ReadonlyMap<string, string> = navReach.labelByKey;

const UNPLACED = Number.MAX_SAFE_INTEGER;

export function menuGroupOf(code: string): string {
  const reach = navReach.byPermission.get(code);
  if (reach) return reach.group;

  const [prefix] = code.split('.');
  return (prefix === undefined ? undefined : navReach.groupByPrefix.get(prefix)) ?? 'other';
}

export function compareByMenuOrder(leftCode: string, rightCode: string): number {
  const groupRank = (code: string) => {
    const index = navReach.groupOrder.indexOf(menuGroupOf(code));
    return index === -1 ? UNPLACED : index;
  };
  const childRank = (code: string) => navReach.byPermission.get(code)?.order ?? UNPLACED;

  return groupRank(leftCode) - groupRank(rightCode) || childRank(leftCode) - childRank(rightCode);
}
